import axios from 'axios';
import { installAdminSessionRefresh } from './adminSessionRefresh';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/admin/invoices`,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// CSRF token helper
function getCsrfToken() {
  const cookieName = 'csrf-token';
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const method = config.method?.toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);
installAdminSessionRefresh(api);

export const invoiceService = {
  /**
   * Get period presets
   */
  async getPeriodPresets() {
    const response = await api.get('/period-presets');
    return response.data;
  },

  /**
   * Preview invoice without creating
   */
  async previewInvoice(data) {
    const response = await api.post('/preview', data);
    return response.data;
  },

  /**
   * Generate invoice
   */
  async generateInvoice(data) {
    const response = await api.post('/generate', data);
    return response.data;
  },

  /**
   * Get list of invoices with filters
   */
  async getInvoices(filters = {}) {
    const params = new URLSearchParams();
    if (filters.workerId) params.append('workerId', filters.workerId);
    if (filters.yearMonth) params.append('yearMonth', filters.yearMonth);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const response = await api.get(`/?${params.toString()}`);
    return response.data;
  },

  /**
   * Get InvoiceRun history
   */
  async getInvoiceRuns(limit = 20, yearMonth = null) {
    const params = new URLSearchParams();
    params.append('limit', limit);
    if (yearMonth) params.append('yearMonth', yearMonth);
    const response = await api.get(`/runs?${params.toString()}`);
    return response.data;
  },

  /**
   * Trigger monthly invoice job manually
   * @param {string|null} yearMonth - YYYY-MM or null for default
   * @param {boolean} dryRun
   * @param {string|null} tenantId - optional: restrict job to workers of this tenant
   */
  async runMonthlyJob(yearMonth = null, dryRun = false, tenantId = null) {
    const response = await api.post('/run-monthly', { yearMonth, dryRun, tenantId });
    return response.data;
  },

  /**
   * Trigger invoice retry job manually
   */
  async runRetryJob(dryRun = false) {
    const response = await api.post('/retry', { dryRun });
    return response.data;
  },

  /**
   * Retry a single invoice (re-send to A-Cube)
   */
  async retrySingleInvoice(invoiceId) {
    const response = await api.post(`/${invoiceId}/retry-single`);
    return response.data;
  },

  /**
   * Get coverage data for a month: per-tenant per-worker invoice status
   */
  async getCoverage(yearMonth) {
    const response = await api.get(`/coverage?yearMonth=${yearMonth}`);
    return response.data;
  },

  /**
   * Get single run detail with invoices generated during that run
   */
  async getRunDetail(runId) {
    const response = await api.get(`/runs/${runId}`);
    return response.data;
  },

  /**
   * Get global billing health: KPI counts + invoices with issues
   */
  async getHealth() {
    const response = await api.get('/health');
    return response.data;
  },

  /**
   * Get invoice by ID
   */
  async getInvoiceById(id) {
    const response = await api.get(`/${id}`);
    return response.data;
  },

  /**
   * Send existing invoice to Acube
   */
  async sendToAcube(id) {
    const response = await api.post(`/${id}/send-to-acube`);
    return response.data;
  },

  /**
   * Get PDF URL for invoice
   */
  /**
   * Download PDF invoice file
   * Returns a blob that can be used to trigger browser download
   */
  async downloadPdf(id) {
    let response;
    try {
      response = await api.get(`/${id}/pdf`, { responseType: 'blob' });
    } catch (err) {
      // Se il server torna un errore, il body è un blob — lo leggiamo come testo
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          throw new Error(json.message || json.error || 'Errore download PDF');
        } catch (parseErr) {
          if (parseErr.message !== 'Errore download PDF') throw parseErr;
          throw new Error(text || 'Errore download PDF');
        }
      }
      throw err;
    }
    return response.data;
  },

  /**
   * List all WorkerPaymentProfiles for the worker selector
   */
  async getWorkerPaymentProfiles() {
    const response = await api.get('/workers/payment-profiles');
    return response.data;
  },

  /**
   * Get worker payment profile (fiscal data)
   */
  async getWorkerPaymentProfile(workerId) {
    const response = await api.get(`/workers/${workerId}/payment-profile`);
    return response.data;
  },

  /**
   * Delete invoice
   */
  async deleteInvoice(id) {
    const response = await api.delete(`/${id}`);
    return response.data;
  },

  /**
   * Get invoices with reversed transfers (for credit notes)
   */
  async getInvoicesWithReversals() {
    const response = await api.get('/with-reversals');
    return response.data;
  },

  /**
   * Get all issued invoices for a worker (for manual credit note creation)
   */
  async getIssuedInvoices(workerPaymentProfileId) {
    const response = await api.get(`/worker/${workerPaymentProfileId}/issued`);
    return response.data;
  },

  /**
   * Generate credit note (DRAFT status)
   * Can be called with data object or legacy separate params
   */
  async createCreditNote(invoiceId, dataOrTransferIds, reasonType, reasonNotes) {
    // Support both new object format and legacy params
    const data = typeof dataOrTransferIds === 'object' && !Array.isArray(dataOrTransferIds)
      ? dataOrTransferIds
      : {
        transferIds: dataOrTransferIds,
        reasonType: reasonType || 'TRANSFER_REVERSAL',
        reasonNotes: reasonNotes || undefined
      };

    const response = await api.post(`/${invoiceId}/credit-note`, data);
    return response.data;
  },

  /**
   * Legacy alias for backward compatibility
   */
  async generateCreditNote(invoiceId, transferIds, reasonType = 'TRANSFER_REVERSAL', reasonNotes = '') {
    return this.createCreditNote(invoiceId, transferIds, reasonType, reasonNotes);
  },

  /**
   * Get all credit notes with optional filters
   */
  async getCreditNotes(filters = {}) {
    const params = new URLSearchParams();
    if (filters.workerPaymentProfileId) params.append('workerPaymentProfileId', filters.workerPaymentProfileId);
    if (filters.status) params.append('status', filters.status);
    if (filters.reason) params.append('reason', filters.reason);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const queryString = params.toString();
    const response = await api.get(`/credit-notes${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  /**
   * Delete a credit note (only DRAFT status)
   */
  async deleteCreditNote(creditNoteId) {
    const response = await api.delete(`/credit-notes/${creditNoteId}`);
    return response.data;
  },

  /**
   * Send credit note to Acube
   */
  async sendCreditNoteToAcube(creditNoteId) {
    const response = await api.post(`/credit-notes/${creditNoteId}/send-to-acube`);
    return response.data;
  },

  /**
   * Download credit note PDF
   */
  async downloadCreditNotePdf(creditNoteId) {
    const response = await api.get(`/credit-notes/${creditNoteId}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

// ── Cron flags (global-settings) ──────────────────────────────────────────────
const globalSettingsApi = axios.create({
  baseURL: `${API_BASE_URL}/api/admin/global-settings`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});
globalSettingsApi.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) config.headers['X-CSRF-Token'] = csrfToken;
  }
  return config;
});
installAdminSessionRefresh(globalSettingsApi);

// ── Tenants list ─────────────────────────────────────────────────────────────
const tenantsApi = axios.create({
  baseURL: `${API_BASE_URL}/api/tenants`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});
installAdminSessionRefresh(tenantsApi);

export const tenantsService = {
  async getAll() {
    const response = await tenantsApi.get('/?limit=200');
    return response.data?.data || [];
  },
};

export const cronFlagsService = {
  async get() {
    const response = await globalSettingsApi.get('/cron-flags');
    return response.data;
  },
  async update(flags) {
    const response = await globalSettingsApi.put('/cron-flags', flags);
    return response.data;
  },
};

export default invoiceService;
